import { DatePipe } from "@angular/common";
import { NotificationService } from "app/_services/notification.service";
import { SpreadsheetValidateService } from "app/_services/spreadsheetValidation.service";
import * as fileSaver from "file-saver";
import { Observable } from "rxjs";
import { handleApiWithNotify } from "shared/utils";
declare const swal: any;

export abstract class SpreadsheetFileHandler {
  isLoaderResult = false;
  file: File | null = null;
  uploadFileName: string = "";
  uploadFileContent: string = "";

  constructor(
    protected spreadsheetService: SpreadsheetValidateService,
    protected datePipe: DatePipe,
    protected notify: NotificationService
  ) {}

  /**
   * Download file with flexible API call and messaging.
   * @param apiCall$ Observable for file download API.
   * @param fileName Name for saving the downloaded file.
   * @param successMessage Message to show on success.
   * @param errorMessage Message to show on failure.
   */
  protected downloadResultFileHelper<T>(
    apiCall$: Observable<T>,
    fileName: string,
    successMessage: string,
    errorMessage: string
  ): void {
    this.isLoaderResult = true;

    handleApiWithNotify(
      apiCall$,
      {
        next: (response: any) => {
          const blob = new Blob([response], {
            type: "application/vnd.ms-excel",
          });

          fileSaver.saveAs(blob, fileName);
        },
        successMessage,
        errorMessage,
      },
      this.notify
    );

    this.isLoaderResult = false;
  }

  protected downloadFileHelper(
    apiCall$: Observable<any>,
    fileNamePrefix: string,
    successMessage: string = "Sample file download process started successfully!",
    errorMessage: string = "Error in downloading the file"
  ): void {
    this.isLoaderResult = true;

    apiCall$.subscribe({
      next: (response: any) => {
        const blob = new Blob([response], {
          type: "application/vnd.ms-excel",
        });

        fileSaver.saveAs(
          blob,
          `${fileNamePrefix}_${this.datePipe.transform(
            new Date(),
            "dd_MMM_yyyy_hh_mm_ss"
          )}.xlsx`
        );

        setTimeout(() => {
          swal({
            title: "",
            text: successMessage,
            type: "success",
            confirmButtonClass: "btn btn-info",
          });
        }, 2000);

        this.isLoaderResult = false;
      },
      error: (err) => {
        this.isLoaderResult = false;
        swal({
          title: "",
          text: errorMessage,
          type: "error",
          confirmButtonClass: "btn btn-info",
        });
      },
    });
  }

  protected uploadFileHelper(
    uploadApiCallFn: (formData: FormData) => Observable<any>,
    file: File,
    downloadResultFileFn: (fileName: string) => void
  ): void {
    this.isLoaderResult = true;

    const formData = new FormData();
    formData.append("file", file, file.name);

    uploadApiCallFn(formData).subscribe({
      next: (data) => {
        const resultFileName = data.additionalMessage;
        const message = data.message;
        const didError = data.didError;

        swal({
          title: "",
          text: message,
          type: didError ? "error" : "success",
          confirmButtonClass: "btn btn-info",
        });

        if (
          typeof resultFileName === "string" &&
          resultFileName.trim().length > 0
        ) {
          setTimeout(() => {
            downloadResultFileFn(resultFileName);
          }, 2500);
        }

        this.isLoaderResult = false;
      },
      error: (error) => {
        const errorMessage = this.getErrorMessageFromApiError(error);

        swal({
          title: "",
          text: errorMessage,
          type: "error",
          confirmButtonClass: "btn btn-info",
        });

        this.isLoaderResult = false;
      },
    });
  }

  /**
   * Extract error message from API error response.
   */
  protected getErrorMessageFromApiError(error: any): string {
    if (error.error?.errors) {
      const allErrors = Object.values(error.error.errors).flat() as string[];
      return allErrors.length > 0 ? allErrors[0] : "Validation error occurred.";
    } else if (typeof error.error === "string") {
      return error.error;
    } else if (error.error?.title) {
      return error.error.title;
    } else {
      return "Something went wrong. Please contact admin.";
    }
  }

  async handleFileUpload(
    event: any,
    onValid: (file: File) => void,
    invalidFileNamePrefix: string
  ): Promise<void> {
    this.isLoaderResult = true;
    this.file = event.target.files[0];

    if (!this.file) return;

    this.uploadFileName = this.file.name;
    this.uploadFileContent = await this.file.text();

    try {
      const result = await this.spreadsheetService.validateSpreadsheet(
        this.file,
        this.uploadFileName
      );

      if (result.isValid) {
        console.log("Spreadsheet is valid.");
        onValid(this.file);
        event.srcElement.value = null;
      } else if (result.errorMessage) {
        this.showError(result.errorMessage);
        this.isLoaderResult = false;
      } else {
        console.log(
          "Spreadsheet is invalid. A modified file is available for download."
        );
        if (result.modifiedFile) {
          this.downloadModifiedFile(result.modifiedFile, invalidFileNamePrefix);
        }
        this.isLoaderResult = false;
      }
    } catch (error) {
      console.error("Error during file validation:", error);
      this.isLoaderResult = false;
    }
  }

  private showError(message: string): void {
    swal({
      title: "Error",
      text: message,
      type: "error",
      confirmButtonClass: "btn btn-info",
    });
  }

  private downloadModifiedFile(file: Blob, prefix: string): void {
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(file);
    downloadLink.download = `${prefix}_invalid_sheet_${this.datePipe.transform(
      new Date(),
      "dd_MMM_yyyy_hh_mm_ss"
    )}.xlsx`;
    downloadLink.click();
  }
}
